import {
  EDLRenderMaterial,
  BasicRenderMaterial,
  EDLRenderTarget,
  BasicRenderTarget,
  OutputRenderMaterial
} from ".";
import {
  Camera,
  Material,
  Mesh,
  MeshBasicMaterial,
  PerspectiveCamera,
  PlaneBufferGeometry,
  Scene,
  Texture,
  Vector2,
  WebGLRenderer,
  WebGLRenderTarget
} from "three";

export class RenderPass {
  private readonly screenScene: Scene;
  private readonly screenQuad: Mesh;
  private readonly camera: Camera;

  constructor() {
    const geometry = new PlaneBufferGeometry(2, 2);
    const material = new MeshBasicMaterial({
      depthTest: true,
      depthWrite: true,
      transparent: true
    });

    this.screenScene = new Scene();
    this.screenQuad = new Mesh(geometry, material);
    this.screenScene.add(this.screenQuad);
    this.camera = new Camera();
  }

  render(renderer, material: Material, target = null) {
    this.screenQuad.material = material;
    renderer.setRenderTarget(target);
    renderer.render(this.screenScene, this.camera);
  }
}

export class RenderUtils {
  private renderer: WebGLRenderer;
  private camera: PerspectiveCamera;
  private screenPass = new RenderPass()
  private targets: WebGLRenderTarget[] = [];
  private pixelRatio;

  private basicMaterial: BasicRenderMaterial;
  private basicRenderTarget: BasicRenderTarget;

  private EDLMaterial: EDLRenderMaterial;
  private EDLRenderTarget: EDLRenderTarget;

  private outputMaterial: OutputRenderMaterial;
  private outputRenderTarget: BasicRenderTarget;

  private previousRenderTarget: BasicRenderTarget;

  constructor(renderer, camera, pixelRatio=1.0) {
    this.renderer = renderer;
    this.camera = camera;
    this.pixelRatio = pixelRatio;

    this.initTargets();
  }

  get EDLSupport() : boolean {
    let capabilities = this.renderer.capabilities;
    let supported = (capabilities.maxVaryings > 8)
      && capabilities.floatFragmentTextures;

    return supported;
  }

  get renderTarget() {
    return this.EDLSupport
      ? this.EDLRenderTarget
      : this.basicRenderTarget;
  }

  get renderMaterial() {
    return this.EDLSupport
      ? this.EDLMaterial
      : this.basicMaterial;
  }

  get isWebGL2() {
    let capabilities = this.renderer.capabilities;
    return capabilities.isWebGL2;
  }

  get width() {
    let renderSize = new Vector2();
    this.renderer.getSize(renderSize);
    return renderSize.x;
  }

  get height() {
    let renderSize = new Vector2();
    this.renderer.getSize(renderSize);
    return renderSize.y;
  }

  initTargets() {
    this.basicMaterial = new BasicRenderMaterial();
    this.basicRenderTarget = new BasicRenderTarget(
      this.width, this.height);
    this.addTarget(this.basicRenderTarget);

    this.outputMaterial = new OutputRenderMaterial();
    this.outputRenderTarget = new BasicRenderTarget(
      this.width, this.height);
    this.addTarget(this.outputRenderTarget);

    if (this.EDLSupport) {
      this.EDLMaterial = new EDLRenderMaterial(
        this.width,
        this.height,
        this.camera.near,
        this.camera.far,
        this.pixelRatio
      );

      this.EDLRenderTarget = new EDLRenderTarget(
        this.width, this.height, this.isWebGL2);
      this.addTarget(this.EDLRenderTarget);
    } else {
      this.EDLMaterial = null;
      this.EDLRenderTarget = null;

      console.log("EDL Not Supported");
    }
  }

  update() {
    this.updateEDLValues();
    this.resizeTextures();
    this.increaseProgress();

    this.renderer.setRenderTarget(this.outputRenderTarget);
    this.renderer.clear();

    this.renderer.setRenderTarget(this.renderTarget);
    this.renderer.clear();

    this.renderer.setRenderTarget(null);
    this.renderer.clear();
  }

  addTarget(target) {
    this.targets.push(target);
  }

  setCamera(camera) {
    this.camera = camera;
  }

  increaseProgress() {
    const totalFrames = 30.0;
    this.outputMaterial.progress += 1.0/totalFrames;
    this.outputMaterial.progress = Math.min(
      1.0, this.outputMaterial.progress);
  }

  /** @private */
  resizeTextures() {
    let width = this.width;
    let height = this.height;

    this.targets.forEach(target => {
      if (!target) {
        return;
      }

      let w = target.width;
      let h = target.height;
      if ((w !== width) || (h !== height)) {
        target.dispose();
        target.setSize(width, height);
      }
    });
  }

  /** @private */
  updateEDLValues() {
    if (!this.EDLMaterial) return;

    this.EDLMaterial.far = this.camera.far;
    this.EDLMaterial.screenWidth = this.width;
    this.EDLMaterial.screenHeight = this.height;
  }

  updateMaterial(material, values) {
    let keys = Object.keys(values);
    keys.forEach(key => {
      material[key] = values[key];
    });
  }

  textureFromImage(imageData: string): Promise<Texture> {
    return new Promise(resolve => {
      const image = new Image();
      image.onload = () =>  {
        const texture = new Texture(image);
        resolve(texture);
      };
      image.src = imageData;
    });
  }

  setRenderTarget(clear: boolean) {
    this.renderer.setRenderTarget(this.renderTarget);
    if (!clear) return;

    this.renderer.clear();
  }

  /** Render scene to renderTarget */
  renderScene(scene: Scene) {
    this.renderer.render(scene, this.camera);
  }

  /** Render renderTarget texture to outputRenderTarget */
  renderToTarget(opacity: number, withEDL: boolean) {
    const renderMaterial = withEDL
      ? this.renderMaterial
      : this.basicMaterial;

    renderMaterial.tDepth = this.renderTarget.depthTexture;
    renderMaterial.tImage = this.renderTarget.texture;
    renderMaterial.opacity = opacity;

    this.screenPass.render(
      this.renderer,
      renderMaterial,
      this.outputRenderTarget
    );
  }

  /** Update the previous image texture in outputRenderTarget */
  renderToPrevious() {
    if (this.previousRenderTarget) {
      this.previousRenderTarget.dispose();
    }

    this.outputMaterial.progress = 1.0;

    this.previousRenderTarget = new BasicRenderTarget(this.width, this.height);
    this.outputMaterial.tCurrent = this.outputRenderTarget.texture;
    this.screenPass.render(
      this.renderer,
      this.outputMaterial,
      this.previousRenderTarget
    );

    this.outputMaterial.tPrevious = this.previousRenderTarget.texture;
    this.outputMaterial.progress = 0;
  }

  renderToScreen() {
    this.outputMaterial.tCurrent = this.outputRenderTarget.texture;
    this.screenPass.render(this.renderer, this.outputMaterial);
  }
}
